import React, { useMemo } from "react";
import moment from "moment";
import { isAbsent, isPresent } from "./StaffAnalyticsTab";
import TabKpiRow from "./TabKpiRow";
import AnalyticsDayHeatmap   from "./AnalyticsDayHeatmap";
import AnalyticsNetworkTrend from "./AnalyticsNetworkTrend";

const DAYS = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];

const isExpected = (att) =>
  att?.status_value !== "on_leave" && att?.status_value !== "day_off";

const computePatternKpis = (allShifts, dateFrom, dateTo) => {
  const dayMap = {};
  DAYS.forEach((d) => { dayMap[d] = { exp: 0, absent: 0 }; });
  allShifts.forEach(({ att, date }) => {
    if (!date || !isExpected(att)) return;
    const day = moment(date, "YYYY-MM-DD").format("dddd");
    if (!dayMap[day]) return;
    dayMap[day].exp++;
    if (isAbsent(att)) dayMap[day].absent++;
  });
  const dayRates = DAYS
    .filter((d) => dayMap[d].exp > 0)
    .map((d) => ({ day: d, rate: Math.round((dayMap[d].absent / dayMap[d].exp) * 100) }));

  const worst = dayRates.length > 0 ? dayRates.reduce((a, b) => b.rate > a.rate ? b : a) : null;
  const best  = dayRates.length > 0 ? dayRates.reduce((a, b) => b.rate < a.rate ? b : a) : null;

  const start = dateFrom ? moment(dateFrom, "YYYY-MM-DD").startOf("isoWeek") : null;
  const end   = dateTo   ? moment(dateTo,   "YYYY-MM-DD").endOf("isoWeek")   : null;
  const buckets = {};
  if (start && end) {
    let cur = start.clone();
    while (cur.isSameOrBefore(end)) {
      buckets[cur.format("YYYY-[W]WW")] = { exp: 0, pres: 0 };
      cur.add(1, "week");
    }
    allShifts.forEach(({ att, date }) => {
      if (!date || !isExpected(att)) return;
      const key = moment(date, "YYYY-MM-DD").format("YYYY-[W]WW");
      if (!buckets[key]) return;
      buckets[key].exp++;
      if (isPresent(att)) buckets[key].pres++;
    });
  }
  const weeks = Object.values(buckets).map((b) => ({
    rate: b.exp > 0 ? Math.round((b.pres / b.exp) * 100) : 0,
  }));
  let trendDir = "→ Flat";
  if (weeks.length >= 2) {
    const diff = weeks[weeks.length - 1].rate - weeks[0].rate;
    if (diff > 2)  trendDir = "↑ Improving";
    if (diff < -2) trendDir = "↓ Declining";
  }

  return [
    { label: "Worst Day",       value: worst ? worst.day.slice(0,3) : "—", mod: worst && worst.rate > 30 ? "red" : "orange", sub: worst ? `${worst.rate}% absent` : undefined },
    { label: "Best Day",        value: best  ? best.day.slice(0,3)  : "—", mod: "green",  sub: best ? `${best.rate}% absent` : undefined },
    { label: "Trend Direction", value: trendDir, mod: trendDir.startsWith("↑") ? "green" : trendDir.startsWith("↓") ? "red" : "blue" },
    { label: "Weeks Analyzed",  value: weeks.length, mod: "blue" },
  ];
};

const AnalyticsPatternsTab = ({ allShifts = [], dateFrom, dateTo }) => {
  const kpiCards = useMemo(() => computePatternKpis(allShifts, dateFrom, dateTo), [allShifts, dateFrom, dateTo]);

  return (
    <div>
      <TabKpiRow cards={kpiCards} />
      <div className="sta-section">
        <div className="sta-section__title">Day-of-Week Absence Heatmap</div>
        <div className="sta-section__sub">
          Which days of the week consistently have the worst absenteeism? Rows = role, columns = day.
        </div>
        <AnalyticsDayHeatmap allShifts={allShifts} dateFrom={dateFrom} dateTo={dateTo} />
      </div>
      <div className="sta-section">
        <div className="sta-section__title">Network Attendance by Week</div>
        <div className="sta-section__sub">
          Week-by-week network attendance rate. Is the problem getting better or worse?
        </div>
        <AnalyticsNetworkTrend allShifts={allShifts} dateFrom={dateFrom} dateTo={dateTo} />
      </div>
    </div>
  );
};

export default AnalyticsPatternsTab;
